/**
 * Live preview for pe3d: what to tell a running game when the variant changes.
 *
 * The release's live template is its preview network built with the engine's
 * QA bridge (playengine3d src/debug/bridge.ts, protocol 2), so instead of a
 * reload an edit becomes one or more bridge requests:
 *
 *   config      → set-config { patch }   the full effective config; the engine
 *                                        applies only what changed and restarts
 *                                        by itself for `apply: "restart"` fields
 *   a slot file → set-asset { slot, files }
 *   storeLinks, endType, sipPlayable → set-playable { … }
 *
 * Some edits cannot be sent: the bridge cannot UNSET a playable setting, and
 * the loading screen only exists before the first frame. Those rebuild the
 * frame. An invalid config is held back until it is valid again — the engine
 * would refuse the whole patch — so the running game keeps its last good state.
 */

import _ from "lodash";

export const BRIDGE_PREFIX = "playengine3d:";
export const BRIDGE_PROTOCOL = 2;

const PLAYABLE_KEYS = ["endType", "sipPlayable"];

/**
 * @param {object} applied  the variant the running game is showing
 * @param {object} next     the draft
 * @param {object} release  { code, bundle } of the variant's release
 * @returns {{ rebuild: boolean, config: object|null, playable: object|null, slots: Array<{slot, ref}>, applied: object }}
 *   `applied` is what the game will show once the requests land (an invalid
 *   config stays at its previous value there, so it is sent once it is valid).
 */
export function planLiveUpdate(applied, next, release) {
  const prev = applied || {};
  const schema = release.bundle.schema;
  const plan = { rebuild: false, config: null, playable: null, slots: [], applied: { ...next } };

  if (!_.isEqual(prev.config || {}, next.config || {})) {
    if (release.code.validateVariant(schema, { config: next.config || {} }).length === 0) {
      plan.config = release.code.effectiveConfig(schema.config, next.config || {});
    } else if (prev.config === undefined) {
      delete plan.applied.config;
    } else {
      plan.applied.config = prev.config;
    }
  }

  const playable = {};
  for (const key of PLAYABLE_KEYS) {
    if (prev[key] === next[key]) continue;
    if (next[key] === undefined) plan.rebuild = true;
    else playable[key] = next[key];
  }
  const prevLinks = prev.storeLinks || {};
  const nextLinks = next.storeLinks || {};
  if (!_.isEqual(prevLinks, nextLinks)) {
    if (Object.keys(prevLinks).some((platform) => !nextLinks[platform])) plan.rebuild = true;
    else playable.storeLinks = nextLinks;
  }
  if (Object.keys(playable).length) plan.playable = playable;

  const slots = _.union(Object.keys(prev.assets || {}), Object.keys(next.assets || {}));
  for (const slot of slots) {
    const ref = (next.assets || {})[slot] || null;
    if (!_.isEqual((prev.assets || {})[slot] || null, ref)) plan.slots.push({ slot, ref });
  }
  return plan;
}

/** Whether a window message is a bridge push or reply. */
export const isBridgeMessage = (data) => Boolean(data && typeof data.type === "string" && data.type.startsWith(BRIDGE_PREFIX));
